import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FiTrendingUp,
  FiShoppingBag,
  FiPackage,
  FiTruck,
  FiDollarSign,
  FiUsers,
  FiFileText,
  FiShoppingCart,
  FiBox,
  FiArrowRight,
  FiRefreshCcw,
  FiAlertTriangle,
} from "react-icons/fi";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
} from "recharts";

import AttendanceOverview from "./components/AttendanceOverview";
import BirthdayList from "./components/BirthdayList";
import ActivityTimeline from "./components/ActivityTimeline";
import StatCard from "./components/StatCard";

import { apiRequest } from "../../services/api";
import { useAuth } from "../../context/AuthContext";
import { hasPermission } from "../../utils/permissions";

const COLORS = ["#7A2238", "#D4AF37", "#2563EB", "#16A34A", "#F59E0B"];

function indiaDate() {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date());

  const year = parts.find((item) => item.type === "year")?.value;
  const month = parts.find((item) => item.type === "month")?.value;
  const day = parts.find((item) => item.type === "day")?.value;

  return `${year}-${month}-${day}`;
}

function addDays(dateValue, amount) {
  const date = new Date(`${dateValue}T12:00:00.000Z`);
  date.setUTCDate(date.getUTCDate() + amount);

  return date.toISOString().slice(0, 10);
}

function monthStart(dateValue) {
  return `${dateValue.slice(0, 7)}-01`;
}

function yearStart(dateValue) {
  return `${dateValue.slice(0, 4)}-01-01`;
}

function formatMoney(value) {
  return `₹${Number(value || 0).toLocaleString("en-IN", {
    maximumFractionDigits: 2,
  })}`;
}

function formatNumber(value) {
  return Number(value || 0).toLocaleString("en-IN", {
    maximumFractionDigits: 2,
  });
}

function formatDate(value) {
  if (!value) return "—";

  return new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "short",
  }).format(new Date(`${value}T00:00:00.000Z`));
}

function getNumber(object, keys) {
  for (const key of keys) {
    const value = object?.[key];

    if (value !== undefined && value !== null && !Number.isNaN(Number(value))) {
      return Number(value);
    }
  }

  return 0;
}

function getList(response, key) {
  if (Array.isArray(response?.[key])) return response[key];
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response)) return response;

  return [];
}

function EmptyState({ text }) {
  return (
    <div className="flex h-52 items-center justify-center text-sm text-gray-400">
      {text}
    </div>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const { accessToken, user } = useAuth();

  const [data, setData] = useState({
    today: null,
    month: null,
    year: null,
    recent: null,
    inventory: null,
    purchases: null,
    mainDashboard: null,
  });

  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState("");

  const today = useMemo(() => indiaDate(), []);
  const permissions = useMemo(
    () => ({
      dashboard: hasPermission(user, "dashboard.view"),
      business: hasPermission(user, "analytics.view"),
      inventory: hasPermission(user, "analytics.inventory"),
      purchases: hasPermission(user, "analytics.purchases"),
      employees: hasPermission(user, "employees.view"),
      attendance:
        hasPermission(user, "attendance.view_team") ||
        hasPermission(user, "attendance.manage"),
      leaveReview: hasPermission(user, "leave.approve"),
      payroll: hasPermission(user, "payroll.view"),
    }),
    [user],
  );

  const loadDashboard = useCallback(
    async (refresh = false) => {
      if (!accessToken) return;

      if (refresh) {
        setIsRefreshing(true);
      } else {
        setIsLoading(true);
      }

      setError("");

      const fetchReport = async (path) => {
        try {
          return await apiRequest(path, { accessToken });
        } catch {
          return null;
        }
      };

      try {
        const todayFrom = today;
        const monthFrom = monthStart(today);
        const yearFrom = yearStart(today);
        const lastSevenDaysFrom = addDays(today, -6);

        const [
          todayReport,
          monthReport,
          yearReport,
          recentReport,
          inventoryReport,
          purchaseReport,
          mainDashboard,
        ] = await Promise.all([
          permissions.business
            ? fetchReport(`/reports/dashboard?from=${todayFrom}&to=${today}`)
            : Promise.resolve(null),
          permissions.business
            ? fetchReport(`/reports/dashboard?from=${monthFrom}&to=${today}`)
            : Promise.resolve(null),
          permissions.business
            ? fetchReport(`/reports/dashboard?from=${yearFrom}&to=${today}`)
            : Promise.resolve(null),
          permissions.business
            ? fetchReport(
                `/reports/dashboard?from=${lastSevenDaysFrom}&to=${today}`,
              )
            : Promise.resolve(null),
          permissions.inventory
            ? fetchReport(`/reports/inventory?from=${monthFrom}&to=${today}`)
            : Promise.resolve(null),
          permissions.purchases
            ? fetchReport(`/reports/purchases?from=${todayFrom}&to=${today}`)
            : Promise.resolve(null),
          permissions.dashboard
            ? fetchReport("/dashboard/overview")
            : Promise.resolve(null),
        ]);

        if (permissions.business && !todayReport && !monthReport && !yearReport) {
          throw new Error("Unable to load dashboard data.");
        }

        setData({
          today: todayReport,
          month: monthReport,
          year: yearReport,
          recent: recentReport,
          inventory: inventoryReport,
          purchases: purchaseReport,
          mainDashboard,
        });
      } catch (requestError) {
        setError(requestError.message || "Unable to load dashboard data.");
      } finally {
        setIsLoading(false);
        setIsRefreshing(false);
      }
    },
    [accessToken, permissions, today],
  );

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const todayMetrics = data.today?.metrics || {};
  const monthMetrics = data.month?.metrics || {};
  const yearMetrics = data.year?.metrics || {};
  const inventoryMetrics = data.inventory?.metrics || {};
  const purchaseMetrics = data.purchases?.metrics || {};

  const inventoryValue = getNumber(inventoryMetrics, [
    "inventoryValue",
    "totalInventoryValue",
    "totalValue",
    "stockValue",
  ]);

  const inventoryQuantity = getNumber(inventoryMetrics, [
    "availableQuantity",
    "availableStock",
    "inventoryQuantity",
    "totalQuantity",
    "quantityOnHand",
    "stockQuantity",
  ]);

  const todayPurchaseValue = getNumber(purchaseMetrics, [
    "purchaseValue",
    "totalPurchaseValue",
    "totalPurchases",
    "receivedValue",
    "totalReceivedValue",
  ]);

  const todayPurchaseQuantity = getNumber(purchaseMetrics, [
    "itemsPurchased",
    "purchaseQuantity",
    "receivedQuantity",
    "totalReceivedQuantity",
    "quantityReceived",
  ]);

  const salesTrend = getList(data.recent, "salesTrend").map((item) => ({
    day: formatDate(item.date),
    sales: Number(item.sales || 0),
  }));

  const inventoryStatus = useMemo(() => {
    const status = getList(data.inventory, "stockStatus");

    if (status.length) {
      return status
        .map((item) => ({
          name: item.name || item.status || "Stock",
          value: Number(item.value || item.quantity || 0),
        }))
        .filter((item) => item.value > 0);
    }

    const available = getNumber(inventoryMetrics, [
      "availableQuantity",
      "availableStock",
    ]);

    const reserved = getNumber(inventoryMetrics, [
      "reservedQuantity",
      "reservedStock",
    ]);

    return [
      { name: "Available", value: available },
      { name: "Reserved", value: reserved },
    ].filter((item) => item.value > 0);
  }, [data.inventory, inventoryMetrics]);

  const paymentMethods = getList(data.month, "paymentMethods");
  const totalPaymentValue = paymentMethods.reduce(
    (sum, item) => sum + Number(item.value || 0),
    0,
  );

  const topProducts = getList(data.month, "topProducts");
  const topCustomers = getList(data.month, "topCustomers");
  const lowStock = getList(data.inventory, "lowStock").length
    ? getList(data.inventory, "lowStock")
    : getList(data.month, "lowStock");

  const pendingPurchaseOrders = getList(data.purchases, "purchases")
    .filter((item) =>
      ["DRAFT", "SUBMITTED", "APPROVED", "PARTIALLY_RECEIVED"].includes(
        item.status,
      ),
    )
    .slice(0, 5);

  const storeSales = getList(data.month, "storeSales");
  const recentTransactions = getList(data.today, "recentTransactions").slice(
    0,
    5,
  );

  const quickActions = [
    {
      title: "New Sale",
      icon: <FiShoppingCart size={24} />,
      path: "/pos/dashboard",
      visible: hasPermission(user, "pos.sell"),
    },
    {
      title: "Purchase Order",
      icon: <FiShoppingBag size={24} />,
      path: "/admindashboard/purchases/create",
      visible: hasPermission(user, "purchases.create"),
    },
    {
      title: "Receive Stock",
      icon: <FiPackage size={24} />,
      path: "/admindashboard/purchases/grn/create",
      visible: hasPermission(user, "purchases.receive"),
    },
    {
      title: "Add Product",
      icon: <FiBox size={24} />,
      path: "/admindashboard/masters/products/add",
      visible: hasPermission(user, "products.create"),
    },
    {
      title: "Add Customer",
      icon: <FiUsers size={24} />,
      path: "/admindashboard/sales/customers",
      visible: hasPermission(user, "customers.create"),
    },
    {
      title: "Analytics",
      icon: <FiFileText size={24} />,
      path: "/admindashboard/analytics",
      visible: permissions.business,
    },
  ];

  const stats = [
    {
      title: "Today's Sales",
      value: formatMoney(todayMetrics.revenue),
      subtitle: `${formatNumber(todayMetrics.orders)} bills today`,
      bgColor: "text-green-600",
      iconBg: "bg-green-100",
      icon: <FiTrendingUp size={28} className="text-green-600" />,
      visible: permissions.business,
    },
    {
      title: "Today's Sale Qty",
      value: formatNumber(todayMetrics.productsSold),
      subtitle: "Products sold today",
      bgColor: "text-blue-600",
      iconBg: "bg-blue-100",
      icon: <FiShoppingCart size={28} className="text-blue-600" />,
      visible: permissions.business,
    },
    {
      title: "Today's Purchase",
      value: formatMoney(todayPurchaseValue),
      subtitle: "Received purchase value",
      bgColor: "text-orange-600",
      iconBg: "bg-orange-100",
      icon: <FiShoppingBag size={28} className="text-orange-600" />,
      visible: permissions.purchases,
    },
    {
      title: "Today's Purchase Qty",
      value: formatNumber(todayPurchaseQuantity),
      subtitle: "Products received today",
      bgColor: "text-indigo-600",
      iconBg: "bg-indigo-100",
      icon: <FiTruck size={28} className="text-indigo-600" />,
      visible: permissions.purchases,
    },
    {
      title: "Inventory Value",
      value: formatMoney(inventoryValue),
      subtitle: "Current stock value",
      bgColor: "text-purple-600",
      iconBg: "bg-purple-100",
      icon: <FiPackage size={28} className="text-purple-600" />,
      visible: permissions.inventory,
    },
    {
      title: "Inventory Qty",
      value: formatNumber(inventoryQuantity),
      subtitle: "Available units",
      bgColor: "text-cyan-600",
      iconBg: "bg-cyan-100",
      icon: <FiPackage size={28} className="text-cyan-600" />,
      visible: permissions.inventory,
    },
    {
      title: "MTD Sales",
      value: formatMoney(monthMetrics.revenue),
      subtitle: "Month to date",
      bgColor: "text-pink-600",
      iconBg: "bg-pink-100",
      icon: <FiDollarSign size={28} className="text-pink-600" />,
      visible: permissions.business,
    },
    {
      title: "YTD Sales",
      value: formatMoney(yearMetrics.revenue),
      subtitle: "Year to date",
      bgColor: "text-emerald-600",
      iconBg: "bg-emerald-100",
      icon: <FiTrendingUp size={28} className="text-emerald-600" />,
      visible: permissions.business,
    },
  ];

  const activityFooter = permissions.attendance
    ? { path: "/admindashboard/attendance", label: "View attendance" }
    : permissions.leaveReview
      ? { path: "/admindashboard/leave", label: "View leave requests" }
      : permissions.payroll
        ? { path: "/admindashboard/payroll", label: "View payroll" }
        : permissions.employees
          ? { path: "/admindashboard/employees", label: "View employees" }
          : null;

  return (
    <div className="space-y-6">
      <div className="rounded-3xl bg-gradient-to-r from-primary-800 to-primary-700 p-8 text-white">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h1 className="text-3xl font-bold md:text-4xl">
              Welcome Back, {user?.email?.split("@")[0]}
            </h1>

            <p className="mt-3 text-lg text-primary-100">
              Here&apos;s what&apos;s happening across your business today.
            </p>
          </div>

          <div className="flex items-center gap-4">
            <button
              type="button"
              onClick={() => loadDashboard(true)}
              disabled={isRefreshing}
              className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/10 hover:bg-white/20 disabled:opacity-60"
              title="Refresh dashboard"
            >
              <FiRefreshCcw className={isRefreshing ? "animate-spin" : ""} />
            </button>

            <div className="rounded-3xl bg-white/10 p-5 backdrop-blur">
              <p className="text-sm uppercase tracking-widest">Today</p>

              <h2 className="mt-2 text-xl font-bold md:text-2xl">
                {new Intl.DateTimeFormat("en-IN", {
                  day: "2-digit",
                  month: "short",
                  year: "numeric",
                }).format(new Date())}
              </h2>
            </div>
          </div>
        </div>
      </div>

      {error ? (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-5 py-4 text-sm text-red-700">
          {error}
        </div>
      ) : null}

      <div className="grid grid-cols-2 gap-5 md:grid-cols-3 xl:grid-cols-6">
        {quickActions.filter((action) => action.visible).map((action) => (
          <button
            key={action.title}
            type="button"
            onClick={() => navigate(action.path)}
            className="group rounded-3xl bg-white p-6 text-left shadow-sm transition hover:-translate-y-1 hover:shadow-xl"
          >
            <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-primary-800/10 text-primary-800">
              {action.icon}
            </div>

            <div className="flex items-center justify-between gap-2">
              <h3 className="font-semibold">{action.title}</h3>
              <FiArrowRight className="transition group-hover:translate-x-1" />
            </div>
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-4">
        {stats.filter((item) => item.visible).map((item) => (
          <StatCard key={item.title} {...item} />
        ))}
      </div>

      {isLoading ? (
        <div className="rounded-3xl bg-white p-12 text-center text-gray-500 shadow-sm">
          Loading live dashboard data…
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-6 xl:grid-cols-12">
            {permissions.business ? <section className="xl:col-span-8 rounded-3xl bg-white p-6 shadow-sm">
              <h2 className="text-xl font-bold">Daily Sales Trend</h2>
              <p className="mt-1 text-gray-500">
                Sales performance over the last seven days.
              </p>

              {salesTrend.length ? (
                <ResponsiveContainer width="100%" height={340}>
                  <AreaChart data={salesTrend}>
                    <defs>
                      <linearGradient
                        id="salesGradient"
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop
                          offset="0%"
                          stopColor="#7A2238"
                          stopOpacity={0.35}
                        />
                        <stop
                          offset="100%"
                          stopColor="#7A2238"
                          stopOpacity={0}
                        />
                      </linearGradient>
                    </defs>

                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="day" />
                    <YAxis tickFormatter={(value) => `₹${value / 1000}k`} />
                    <Tooltip formatter={(value) => formatMoney(value)} />

                    <Area
                      type="monotone"
                      dataKey="sales"
                      stroke="#7A2238"
                      strokeWidth={3}
                      fill="url(#salesGradient)"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <EmptyState text="No sales recorded in the last seven days." />
              )}
            </section> : null}

            {permissions.inventory ? <section className="xl:col-span-4 rounded-3xl bg-white p-6 shadow-sm">
              <h2 className="text-xl font-bold">Inventory Status</h2>

              {inventoryStatus.length ? (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={inventoryStatus}
                      dataKey="value"
                      nameKey="name"
                      innerRadius={70}
                      outerRadius={110}
                    >
                      {inventoryStatus.map((item, index) => (
                        <Cell
                          key={item.name}
                          fill={COLORS[index % COLORS.length]}
                        />
                      ))}
                    </Pie>

                    <Legend />
                    <Tooltip formatter={(value) => formatNumber(value)} />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <EmptyState text="Inventory status will appear here." />
              )}
            </section> : null}
          </div>

          {permissions.business ? <div className="grid grid-cols-1 gap-6 xl:grid-cols-12">
            <section className="xl:col-span-7 rounded-3xl bg-white p-6 shadow-sm">
              <h2 className="text-xl font-bold">Sales by Store</h2>

              {storeSales.length ? (
                <ResponsiveContainer width="100%" height={320}>
                  <BarChart data={storeSales}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis tickFormatter={(value) => `₹${value / 1000}k`} />
                    <Tooltip formatter={(value) => formatMoney(value)} />
                    <Bar
                      dataKey="value"
                      name="Revenue"
                      fill="#7A2238"
                      radius={[8, 8, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <EmptyState text="Store sales data will appear here." />
              )}
            </section>

            <section className="xl:col-span-5 rounded-3xl bg-white p-6 shadow-sm">
              <h2 className="text-xl font-bold">Payment Modes</h2>

              {paymentMethods.length ? (
                <div className="mt-6 space-y-5">
                  {paymentMethods.map((payment) => {
                    const amount = Number(payment.value || 0);
                    const percentage = totalPaymentValue
                      ? (amount / totalPaymentValue) * 100
                      : 0;

                    return (
                      <div key={payment.name}>
                        <div className="mb-2 flex justify-between gap-4">
                          <span className="font-medium">
                            {String(payment.name || "Other").replace(/_/g, " ")}
                          </span>

                          <span className="font-semibold">
                            {formatMoney(amount)}
                          </span>
                        </div>

                        <div className="h-3 overflow-hidden rounded-full bg-gray-200">
                          <div
                            className="h-full rounded-full bg-primary-800"
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <EmptyState text="No payment data for this month." />
              )}
            </section>
          </div> : null}

          <div className="grid grid-cols-1 gap-6 xl:grid-cols-12">
            {permissions.business ? <section className="xl:col-span-4 rounded-3xl bg-white p-6 shadow-sm">
              <div className="mb-5 flex items-center justify-between">
                <h2 className="text-xl font-bold">Top Selling Products</h2>

                <button
                  type="button"
                  onClick={() => navigate("/admindashboard/analytics/sales")}
                  className="text-sm font-semibold text-primary-800"
                >
                  View All
                </button>
              </div>

              {topProducts.length ? (
                topProducts.slice(0, 5).map((product) => (
                  <div
                    key={product.variantId || product.id}
                    className="mb-3 flex items-center justify-between rounded-2xl border p-4"
                  >
                    <div>
                      <p className="font-semibold">{product.name}</p>
                      <p className="text-sm text-gray-500">
                        {formatNumber(product.sold)} units sold
                      </p>
                    </div>

                    <p className="font-bold text-primary-800">
                      {formatMoney(product.revenue)}
                    </p>
                  </div>
                ))
              ) : (
                <EmptyState text="No product sales this month." />
              )}
            </section> : null}

            {permissions.business ? <section className="xl:col-span-4 rounded-3xl bg-white p-6 shadow-sm">
              <div className="mb-5 flex items-center justify-between">
                <h2 className="text-xl font-bold">Top Customers</h2>

                <button
                  type="button"
                  onClick={() => navigate("/admindashboard/analytics/customers")}
                  className="text-sm font-semibold text-primary-800"
                >
                  View All
                </button>
              </div>

              {topCustomers.length ? (
                topCustomers.slice(0, 5).map((customer) => (
                  <div
                    key={customer.id || customer.name}
                    className="mb-3 flex items-center justify-between rounded-2xl border p-4"
                  >
                    <div>
                      <p className="font-semibold">{customer.name}</p>
                      <p className="text-sm text-gray-500">
                        {formatNumber(customer.orders)} orders
                      </p>
                    </div>

                    <p className="font-bold text-primary-800">
                      {formatMoney(customer.spent)}
                    </p>
                  </div>
                ))
              ) : (
                <EmptyState text="No customer sales this month." />
              )}
            </section> : null}

            {(permissions.inventory || permissions.purchases || permissions.business) ? <section className="xl:col-span-4 rounded-3xl bg-white p-6 shadow-sm">
              <h2 className="mb-5 text-xl font-bold">Business Alerts</h2>

              <div className="space-y-4">
                {permissions.inventory ? <button
                  type="button"
                  onClick={() =>
                    navigate("/admindashboard/inventory/low-stock")
                  }
                  className="w-full rounded-2xl bg-red-50 p-4 text-left"
                >
                  <p className="font-semibold text-red-600">
                    <FiAlertTriangle className="mr-2 inline" />
                    {lowStock.length} low-stock product
                    {lowStock.length === 1 ? "" : "s"}
                  </p>
                </button> : null}

                {permissions.purchases ? <button
                  type="button"
                  onClick={() =>
                    navigate("/admindashboard/purchases/orders")
                  }
                  className="w-full rounded-2xl bg-yellow-50 p-4 text-left"
                >
                  <p className="font-semibold text-yellow-700">
                    <FiShoppingBag className="mr-2 inline" />
                    {pendingPurchaseOrders.length} pending purchase order
                    {pendingPurchaseOrders.length === 1 ? "" : "s"}
                  </p>
                </button> : null}

                {permissions.business ? <button
                  type="button"
                  onClick={() => navigate("/admindashboard/analytics")}
                  className="w-full rounded-2xl bg-blue-50 p-4 text-left"
                >
                  <p className="font-semibold text-blue-700">
                    <FiFileText className="mr-2 inline" />
                    Open detailed business reports
                  </p>
                </button> : null}
              </div>
            </section>
            : null}
          </div>

          <div className="grid grid-cols-1 gap-6 xl:grid-cols-12">
            {permissions.inventory ? <section className="xl:col-span-6 rounded-3xl bg-white p-6 shadow-sm">
              <div className="mb-5 flex items-center justify-between">
                <h2 className="text-xl font-bold">Low Stock Products</h2>

                <button
                  type="button"
                  onClick={() =>
                    navigate("/admindashboard/inventory/low-stock")
                  }
                  className="font-semibold text-primary-800"
                >
                  View All
                </button>
              </div>

              {lowStock.length ? (
                lowStock.slice(0, 5).map((item) => (
                  <div
                    key={`${item.variantId}-${item.sku}`}
                    className="mb-3 flex items-center justify-between rounded-2xl border p-4"
                  >
                    <div>
                      <p className="font-semibold">
                        {item.product || item.name}
                      </p>

                      <p className="text-sm text-gray-500">{item.sku}</p>
                    </div>

                    <div className="text-right">
                      <p className="font-bold text-red-600">
                        {formatNumber(
                          item.availableQuantity ?? item.availableStock,
                        )}
                      </p>

                      <p className="text-xs text-gray-500">
                        Min {formatNumber(item.minimumStock)}
                      </p>
                    </div>
                  </div>
                ))
              ) : (
                <EmptyState text="No low-stock items right now." />
              )}
            </section> : null}

            {permissions.purchases ? <section className="xl:col-span-6 rounded-3xl bg-white p-6 shadow-sm">
              <div className="mb-5 flex items-center justify-between">
                <h2 className="text-xl font-bold">Pending Purchase Orders</h2>

                <button
                  type="button"
                  onClick={() =>
                    navigate("/admindashboard/purchases/orders")
                  }
                  className="font-semibold text-primary-800"
                >
                  View All
                </button>
              </div>

              {pendingPurchaseOrders.length ? (
                pendingPurchaseOrders.map((po) => (
                  <div
                    key={po.id}
                    className="mb-3 flex items-center justify-between rounded-2xl border p-4"
                  >
                    <div>
                      <p className="font-semibold">
                        {po.purchaseOrderNumber || po.poNumber || po.code}
                      </p>

                      <p className="text-sm text-gray-500">
                        {po.supplier?.displayName ||
                          po.supplier?.name ||
                          "Supplier"}
                      </p>
                    </div>

                    <p className="font-bold text-primary-800">
                      {formatMoney(po.totalAmount)}
                    </p>
                  </div>
                ))
              ) : (
                <EmptyState text="No pending purchase orders." />
              )}
            </section> : null}
          </div>

          {permissions.business ? <div className="grid grid-cols-1 gap-6 xl:grid-cols-12">
            <section className="xl:col-span-7 rounded-3xl bg-white p-6 shadow-sm">
              <div className="mb-6 flex items-center justify-between">
                <h2 className="text-xl font-bold">Recent POS Bills</h2>

                <button
                  type="button"
                  onClick={() => navigate("/admindashboard/sales/pos-bills")}
                  className="font-semibold text-primary-800"
                >
                  View All
                </button>
              </div>

              {recentTransactions.length ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full">
                    <thead className="bg-[#F8F6F5]">
                      <tr>
                        <th className="px-4 py-3 text-left">Bill</th>
                        <th className="px-4 py-3 text-left">Customer</th>
                        <th className="px-4 py-3 text-left">Store</th>
                        <th className="px-4 py-3 text-left">Payment</th>
                        <th className="px-4 py-3 text-right">Amount</th>
                      </tr>
                    </thead>

                    <tbody>
                      {recentTransactions.map((bill) => (
                        <tr key={bill.id} className="border-b hover:bg-gray-50">
                          <td className="px-4 py-4 font-semibold text-primary-800">
                            {bill.invoice}
                          </td>
                          <td className="px-4 py-4">
                            {bill.customer || "Walk-in Customer"}
                          </td>
                          <td className="px-4 py-4">{bill.store}</td>
                          <td className="px-4 py-4">
                            {String(bill.payment || "UNPAID").replace(
                              /_/g,
                              " ",
                            )}
                          </td>
                          <td className="px-4 py-4 text-right font-bold text-green-600">
                            {formatMoney(bill.amount)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <EmptyState text="No POS bills created today." />
              )}
            </section>

            <section className="xl:col-span-5 rounded-3xl bg-white p-6 shadow-sm">
              <h2 className="mb-6 text-xl font-bold">Store Performance</h2>

              {storeSales.length ? (
                storeSales.map((store) => (
                  <div key={store.name} className="mb-4 rounded-2xl border p-4">
                    <div className="flex justify-between gap-4">
                      <div>
                        <p className="font-semibold">{store.name}</p>
                        <p className="text-sm text-gray-500">
                          Current month sales
                        </p>
                      </div>

                      <p className="font-bold text-primary-800">
                        {formatMoney(store.value)}
                      </p>
                    </div>
                  </div>
                ))
              ) : (
                <EmptyState text="No store sales recorded this month." />
              )}
            </section>
          </div> : null}
        </>
      )}

      {permissions.dashboard && (permissions.employees || data.mainDashboard?.recentActivities?.length) ? <div className="grid grid-cols-1 gap-6 xl:grid-cols-12">
        {permissions.employees ? <div className="xl:col-span-4">
          <BirthdayList
            birthdays={data.mainDashboard?.upcomingBirthdays || []}
            isLoading={isLoading}
          />
        </div> : null}

        {data.mainDashboard?.recentActivities?.length ? <div className="xl:col-span-8">
          <ActivityTimeline
            activities={data.mainDashboard?.recentActivities || []}
            isLoading={isLoading}
            footerPath={activityFooter?.path}
            footerLabel={activityFooter?.label}
          />
        </div> : null}
      </div> : null}

      {permissions.dashboard && permissions.attendance ? <AttendanceOverview
        overview={data.mainDashboard?.attendance}
        isLoading={isLoading}
      /> : null}
    </div>
  );
}
